import { TokenExpiredErrorView } from '@/setup/containers/errors/TokenExpiredErrorView';
import { UserAlreadySet } from '@/setup/containers/user-already-set/UserAlreadySet';
import { ErrorCode } from './constants';
import { ErrorView } from './ErrorView';
import { type ErrorConfig, type ErrorConfigMap, ErrorType } from './types';

export const UNEXPECTED_ERROR_CODE = 'unexpected-error';
export enum GLOBAL_ERRORS {
  ACCOUNT_LOCKED = 'idp-locked', // for authn
  ASSURANCE_ID_NOT_FOUND = 'assurance-id-not-found',
  ASSURANCE_INVALID_GRANT = 'invalid-grant',
  ASSURANCE_IN_PROGRESS = 'assurance-in-progress', // for assurance/start
  ASSURANCE_NOT_STARTED = 'assurance-not-started',
  ASSURANCE_USER_CANNOT_BE_SUPERVISOR = 'user-cannot-be-supervisor',
  DIFFERENT_CUSTOMER = 'different-customer',
  DIRECTORIES_SYNC_ERROR = 'directory-sync-error',
  DOMAIN_ALREADY_EXISTS = 'domain-already-exists',
  DOMAIN_RESERVED = 'reserved-domain',
  ENTRA_ID_AUTH_ERROR = 'entra-id-auth-error',
  FACTORS_INSUFFICIENT = 'factors-insufficient', // for authn
  FATAL = 'fatal',
  IMPLEMENTATION_DEFECT = 'implementation-defect',
  INCOMPLETE_OPERATION = 'incomplete-operation',
  INTERNAL = 'internal',
  INTERNAL_1 = 'INTERNAL',
  INVALID_ARGUMENT = 'invalid-argument',
  INVALID_ENTRA_TENANT_ID = 'invalid-aad-tenant-id',
  INVALID_FILE_TYPE = 'invalid-file-type',
  INVALID_LEGAL_DOCUMENT = 'invalid-legal-document',
  INVALID_PHONE_NUMBER = 'invalid-phone-number', // for /impr-idp-users/v1/save
  INVALID_TENANT_ID = 'invalid-tenant-id',
  INVALID_TENANT_STATE = 'invalid-tenant-state',
  LOGO_UPLOAD_ERROR = 'logo-upload-error',
  NOT_FOUND = 'not-found',
  OFFLINE = 'offline', // for authn
  PERMISSION_DENIED = 'forbidden',
  SESSION_EXPIRED = 'session-expired',
  TENANT_NOT_FOUND = 'tenant-not-found', // for authn
  USER_NOT_FOUND = 'user-not-found', // for authn
}

export enum FULL_PAGE_ERRORS {
  SYSTEM_ERROR = 'SYSTEM_ERROR',
  FATAL_ERROR = 'FATAL_ERROR',
  LINK_DEAD = 'link-dead',
  TOKEN_EXPIRED = 'token-expired',
}

const domainAlreadyExistsErrorConfig: ErrorConfig = {
  errorCode: GLOBAL_ERRORS.DOMAIN_ALREADY_EXISTS,
  type: 'error',
  errorType: ErrorType.GLOBAL,
};

export const errorConfiguration: ErrorConfigMap = {
  // Assurance
  [GLOBAL_ERRORS.ASSURANCE_IN_PROGRESS]: {
    errorCode: GLOBAL_ERRORS.ASSURANCE_IN_PROGRESS,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.ASSURANCE_USER_CANNOT_BE_SUPERVISOR]: {
    errorCode: GLOBAL_ERRORS.ASSURANCE_USER_CANNOT_BE_SUPERVISOR,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [ErrorCode.ID_PROOFING_FAILED]: {
    errorCode: ErrorCode.ID_PROOFING_FAILED,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [ErrorCode.ID_PROOFING_TIMEOUT]: {
    errorCode: ErrorCode.ID_PROOFING_TIMEOUT,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [ErrorCode.ID_PROOFING_POPUP_BLOCKED]: {
    errorCode: ErrorCode.ID_PROOFING_POPUP_BLOCKED,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  //////////////
  [GLOBAL_ERRORS.NOT_FOUND]: {
    errorCode: GLOBAL_ERRORS.NOT_FOUND,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.PERMISSION_DENIED]: {
    errorCode: GLOBAL_ERRORS.PERMISSION_DENIED,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.IMPLEMENTATION_DEFECT]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INTERNAL]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INTERNAL_1]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INVALID_PHONE_NUMBER]: {
    errorCode: GLOBAL_ERRORS.INVALID_PHONE_NUMBER,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INVALID_TENANT_ID]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INVALID_TENANT_STATE]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INCOMPLETE_OPERATION]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.SESSION_EXPIRED]: {
    errorCode: GLOBAL_ERRORS.SESSION_EXPIRED,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.FATAL]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INVALID_FILE_TYPE]: {
    errorCode: GLOBAL_ERRORS.INVALID_FILE_TYPE,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.LOGO_UPLOAD_ERROR]: {
    errorCode: GLOBAL_ERRORS.LOGO_UPLOAD_ERROR,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INVALID_ENTRA_TENANT_ID]: {
    errorCode: GLOBAL_ERRORS.INVALID_ENTRA_TENANT_ID,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.ENTRA_ID_AUTH_ERROR]: {
    errorCode: GLOBAL_ERRORS.ENTRA_ID_AUTH_ERROR,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.DIRECTORIES_SYNC_ERROR]: {
    errorCode: GLOBAL_ERRORS.DIRECTORIES_SYNC_ERROR,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [FULL_PAGE_ERRORS.SYSTEM_ERROR]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.FULL_PAGE,
    errorView: <ErrorView />,
  },
  [FULL_PAGE_ERRORS.FATAL_ERROR]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.FULL_PAGE,
    errorView: <ErrorView />,
  },
  [FULL_PAGE_ERRORS.LINK_DEAD]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.FULL_PAGE,
    errorView: <UserAlreadySet />,
  },
  [FULL_PAGE_ERRORS.TOKEN_EXPIRED]: {
    errorCode: FULL_PAGE_ERRORS.TOKEN_EXPIRED,
    type: 'warning',
    errorType: ErrorType.FULL_PAGE,
    errorView: <TokenExpiredErrorView />,
  },
  [GLOBAL_ERRORS.DOMAIN_ALREADY_EXISTS]: domainAlreadyExistsErrorConfig,
  [GLOBAL_ERRORS.DIFFERENT_CUSTOMER]: domainAlreadyExistsErrorConfig,
  [GLOBAL_ERRORS.DOMAIN_RESERVED]: {
    errorCode: GLOBAL_ERRORS.DOMAIN_RESERVED,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INVALID_LEGAL_DOCUMENT]: {
    errorCode: GLOBAL_ERRORS.INVALID_LEGAL_DOCUMENT,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [ErrorCode.USERS_IMPORT_FAIL]: {
    errorCode: ErrorCode.USERS_IMPORT_FAIL,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [ErrorCode.MISSING_CSV]: {
    errorCode: ErrorCode.MISSING_CSV,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INVALID_ARGUMENT]: {
    errorCode: GLOBAL_ERRORS.INVALID_ARGUMENT,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  // Authn
  [GLOBAL_ERRORS.ACCOUNT_LOCKED]: {
    errorCode: GLOBAL_ERRORS.ACCOUNT_LOCKED,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.FACTORS_INSUFFICIENT]: {
    errorCode: GLOBAL_ERRORS.FACTORS_INSUFFICIENT,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.OFFLINE]: {
    errorCode: GLOBAL_ERRORS.OFFLINE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.TENANT_NOT_FOUND]: {
    errorCode: GLOBAL_ERRORS.TENANT_NOT_FOUND,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.USER_NOT_FOUND]: {
    errorCode: GLOBAL_ERRORS.USER_NOT_FOUND,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [ErrorCode.ALREADY_EXISTS]: {
    errorCode: ErrorCode.ALREADY_EXISTS,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
};
